import { useState } from 'react';
import { Plus, Check, AlertCircle } from 'lucide-react';
import { platformConnectionsApi } from '../lib/api';
import { Button } from './ui';

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface TagCreatorProps {
  connectionId: string;
  /** Called with the created (or existing) tag so the parent can add it to its tag list and optionally select it. */
  onCreated: (tag: Tag) => void;
}

/**
 * Inline "add a new tag" control. Creates a ClickFunnels contact tag on the
 * connection (workspace-wide, so it becomes reusable across every form, quiz,
 * and button), then hands it back to the parent.
 */
export default function TagCreator({ connectionId, onCreated }: TagCreatorProps) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || creating || !connectionId) return;
    setCreating(true);
    setError(null);
    try {
      const res = await platformConnectionsApi.createTag(connectionId, trimmed);
      if (res.success && res.data) {
        onCreated(res.data.tag);
        setName('');
        setJustAdded(res.data.tag.name);
        setTimeout(() => setJustAdded(null), 2500);
      } else {
        setError(res.message || 'Could not create tag');
      }
    } catch {
      setError('Could not create tag');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }}
          placeholder="New tag name…"
          className="ds-input flex-1"
        />
        <Button
          type="button"
          onClick={create}
          disabled={!name.trim() || creating}
          icon={Plus}
          className="flex-shrink-0"
        >
          {creating ? 'Adding…' : 'Add Tag'}
        </Button>
      </div>
      {error && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-danger">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      {justAdded && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-success">
          <Check className="w-3.5 h-3.5" /> Added “{justAdded}” — reusable across all forms, quizzes &amp; buttons.
        </div>
      )}
    </div>
  );
}
